import React, {
  ChangeEvent,
  KeyboardEvent,
  useLayoutEffect,
  useRef,
} from 'react';
import { Input } from 'reactstrap';

import 'style/layout/AutoGrowTextArea.css';

/**
 * A text field for a long text on one line of the PDF: the text wraps when the field is
 * full and the field grows higher with it. A line break cannot be typed, as the PDF prints
 * the text as one paragraph.
 */
const AutoGrowTextArea = ({
  ariaLabel,
  className,
  disabled,
  id,
  maxLength,
  name,
  onChange,
  placeholder,
  required,
  title,
  value,
}: {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  maxLength?: number;
  name?: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  title?: string;
  value: string;
}): JSX.Element => {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // As high as the text needs, at least one line
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }

    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight + 2}px`;
  }, [value]);

  const preventLineBreak = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <Input
      aria-label={ariaLabel}
      className={`text-field auto-grow-text-area ${className ?? ''}`}
      disabled={disabled}
      id={id}
      innerRef={ref}
      maxLength={maxLength}
      name={name}
      onChange={onChange}
      onKeyDown={preventLineBreak}
      placeholder={placeholder}
      required={required}
      rows={1}
      title={title}
      type="textarea"
      value={value}
    />
  );
};

export default AutoGrowTextArea;
